<div class="modal-container">
  <div class="modal-content">
    <h1 class="modal-title">{{ modalTitle }}</h1>

    <div *ngIf="isLoadingCustomers" class="loading-overlay">
      <mat-spinner diameter="30"></mat-spinner>
      <span>Loading customers...</span>
    </div>

    <form [formGroup]="addUserForm" [class.form-loading]="isLoadingCustomers">
      <div class="form-grid">
        <div class="form-column">
          <div class="form-group">
            <label for="firstName">First Name<span class="required">*</span></label>
            <input
              type="text"
              id="firstName"
              formControlName="firstName"
              class="form-control"
              [class.is-invalid]="isFieldInvalid('firstName')"
              placeholder="Enter first name"
              autocomplete="given-name"
              (keydown)="onPersonNameKeydown($event)"
              (input)="onPersonNameInput('firstName', $event)"
            />
            <small class="form-error" *ngIf="getFieldErrorMessage('firstName') as err">{{ err }}</small>
          </div>

          <div class="form-group">
            <label for="lastName">Last Name<span class="required">*</span></label>
            <input
              type="text"
              id="lastName"
              formControlName="lastName"
              class="form-control"
              [class.is-invalid]="isFieldInvalid('lastName')"
              placeholder="Enter last name"
              autocomplete="family-name"
              (keydown)="onPersonNameKeydown($event)"
              (input)="onPersonNameInput('lastName', $event)"
            />
            <small class="form-error" *ngIf="getFieldErrorMessage('lastName') as err">{{ err }}</small>
          </div>

          <div class="form-group">
            <label for="emailId">
              Email ID<span class="required" *ngIf="isAddMode">*</span>
            </label>
            <input
              type="text"
              id="emailId"
              formControlName="emailId"
              class="form-control"
              [class.is-invalid]="isFieldInvalid('emailId')"
              placeholder="Enter email address"
              autocomplete="email"
              inputmode="email"
            />
            <small class="form-error" *ngIf="getFieldErrorMessage('emailId') as err">{{ err }}</small>
          </div>

          <div
            class="form-group form-group--inline form-group--status"
            [class.form-group--active-locked]="!isEditMode"
          >
            <label for="status" class="inline-label">Status</label>
            <span
              class="active-status-checkbox-wrap"
              [class.active-status-checkbox-wrap--locked]="!isEditMode"
            >
              <input
                type="checkbox"
                id="status"
                formControlName="isActive"
                class="form-check-input"
                [class.form-check-input--readonly]="!isEditMode"
                [attr.aria-readonly]="!isEditMode"
              />
            </span>
            <small class="active-status-hint">
              {{
                isEditMode
                  ? 'You can update active status while editing.'
                  : 'New users are created as Active.'
              }}
            </small>
          </div>
        </div>

        <div class="form-column">
          <div class="form-group form-group--inline">
            <label for="enableAdminAccess" class="inline-label">Enable Admin Access</label>
            <input
              type="checkbox"
              id="enableAdminAccess"
              formControlName="enableAdminAccess"
              class="form-check-input"
            />
          </div>

          <div class="form-group customer-assignment-group">
            <label for="customerAssignment">
              Customer Assignment<span class="required">*</span>
            </label>

            <mat-form-field
              appearance="fill"
              class="customer-assignment-field"
              [class.is-invalid]="isFieldInvalid('customerAssignment')"
            >
              <mat-select
                id="customerAssignment"
                formControlName="customerAssignment"
                multiple
                placeholder="Select customers"
                panelClass="customer-assignment-panel"
                (openedChange)="onCustomerPanelOpenedChange($event)"
              >
                <mat-option disabled>
                  <input
                    #customerSearchInput
                    matInput
                    placeholder="Search customers"
                    aria-label="Search customers"
                    [(ngModel)]="customerSearchText"
                    [ngModelOptions]="{ standalone: true }"
                    (click)="$event.stopPropagation()"
                    (mousedown)="$event.stopPropagation()"
                    (keydown)="$event.stopPropagation()"
                  />
                </mat-option>

                <mat-option *ngIf="isLoadingCustomers" disabled>
                  Loading customers...
                </mat-option>

                <mat-option
                  *ngIf="!isLoadingCustomers && filteredCustomerOptions.length === 0"
                  disabled
                >
                  No customers found
                </mat-option>

                <mat-option
                  *ngFor="let customer of filteredCustomerOptions"
                  [value]="customer.id"
                >
                  {{ customer.name }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <div
              class="selected-customer-chips"
              *ngIf="(addUserForm.get('customerAssignment')?.value || []).length > 0"
            >
              <span
                class="customer-chip"
                *ngFor="let customerId of addUserForm.get('customerAssignment')?.value"
              >
                {{ getCustomerName(customerId) }}
                <button
                  type="button"
                  class="chip-remove-btn"
                  (click)="removeCustomer(customerId)"
                  [disabled]="isSubmitting"
                  aria-label="Remove customer"
                >
                  ×
                </button>
              </span>
            </div>

            <small class="form-error" *ngIf="getFieldErrorMessage('customerAssignment') as err">{{ err }}</small>
          </div>

          <div class="form-group user-roles-group">
            <label for="userRoles">User Roles<span class="required">*</span></label>

            <mat-form-field
              appearance="fill"
              class="user-roles-field"
              [class.is-invalid]="isFieldInvalid('userRoles')"
            >
              <mat-select
                id="userRoles"
                formControlName="userRoles"
                multiple
                placeholder="Select User Roles"
                panelClass="user-roles-panel"
                (openedChange)="onRolePanelOpenedChange($event)"
              >
                <mat-option disabled>
                  <input
                    #roleSearchInput
                    matInput
                    placeholder="Search roles"
                    aria-label="Search roles"
                    [(ngModel)]="roleSearchText"
                    [ngModelOptions]="{ standalone: true }"
                    (click)="$event.stopPropagation()"
                    (mousedown)="$event.stopPropagation()"
                    (keydown)="$event.stopPropagation()"
                  />
                </mat-option>

                <mat-option *ngIf="isLoadingRoles" disabled>
                  Loading roles...
                </mat-option>

                <mat-option *ngIf="!isLoadingRoles && filteredRoleOptions.length === 0" disabled>
                  No roles found
                </mat-option>

                <mat-option
                  *ngFor="let role of filteredRoleOptions"
                  [value]="role.id"
                >
                  {{ role.name }}
                </mat-option>
              </mat-select>
            </mat-form-field>

            <div class="selected-role-chips" *ngIf="(addUserForm.get('userRoles')?.value || []).length > 0">
              <span class="role-chip" *ngFor="let roleId of addUserForm.get('userRoles')?.value">
                {{ getRoleName(roleId) }}
                <button
                  type="button"
                  class="chip-remove-btn"
                  (click)="removeRole(roleId)"
                  [disabled]="isSubmitting"
                  aria-label="Remove role"
                >
                  ×
                </button>
              </span>
            </div>

            <small class="form-error" *ngIf="getFieldErrorMessage('userRoles') as err">{{ err }}</small>
          </div>
        </div>
      </div>

      <div class="button-container">
        <button
          mat-stroked-button
          type="button"
          class="btn-cancel"
          (click)="onCancel()"
          [disabled]="isSubmitting"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn-confirm"
          (click)="onSubmit()"
          [disabled]="isSubmitDisabled"
          [attr.aria-disabled]="isSubmitDisabled"
        >
          {{ submitButtonText }}
        </button>
      </div>
    </form>
  </div>
</div>